import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { compare, findIndex } from "@dhx/ts-common/core";
import { DataEvents } from "@dhx/ts-data";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import {
	GridEvents,
	ICell,
	IGrid,
	ISelection,
	IRow,
	ICol,
	ISelectionType,
	GridSelectionEvents,
	IGridSelectionEventsHandlersMap,
	ISelectionConfig,
} from "./types";
import { Id } from "@dhx/ts-common/types";
import { focusManager } from "@dhx/ts-common/FocusManager";

export class Selection implements ISelection {
	public events: IEventSystem<GridSelectionEvents, IGridSelectionEventsHandlersMap>;
	public config: ISelectionConfig;

	protected _grid: IGrid;
	private _gridId: Id;
	protected _selectedCell: ICell;
	protected _oldSelectedCell: ICell;
	protected _selectedCells: ICell[];
	protected _type: ISelectionType;
	protected _multiselection: boolean;

	constructor(grid: IGrid, config?: ISelectionConfig, events?: IEventSystem<any>, gridId?: Id) {
		const types = ["cell", "row", "complex"];

		this._grid = grid;
		this.config = config;
		this._gridId = gridId;
		this._selectedCell = undefined;
		this._oldSelectedCell = undefined;
		this._selectedCells = [];
		this._type = types.includes(this._grid.config.selection) ? this._grid.config.selection : "complex";
		this._multiselection = grid.config.multiselection;

		this.events = events || new EventSystem<GridSelectionEvents>(this);
		this._init();
	}
	setCell(row?: any, col?: any, ctrlUp = false, shiftUp = false) {
		if (this._gridId && focusManager.getFocusId() !== this._gridId) {
			focusManager.setFocusId(this._gridId as string);
		}
		if (
			this.config.disabled ||
			this._grid.config.$editable ||
			(!this._multiselection &&
				this._oldSelectedCell &&
				this._oldSelectedCell.row.id === ((row && row.id) || row) &&
				this._oldSelectedCell.column.id === ((col && col.id) || col)) ||
			(this._multiselection &&
				this._selectedCells.length === 1 &&
				this._selectedCells[0].row.id === ((row && row.id) || row) &&
				this._selectedCells[0].column.id === ((col && col.id) || col))
		) {
			return;
		}
		if ((this._multiselection && !ctrlUp && !shiftUp) || !this._multiselection) {
			this._selectedCells.length && this._removeCells();
			if (this._type !== "row" && this._selectedCells.length) {
				this._grid.paint();
				this._setBrowserFocus();
				return;
			}
		}
		if (
			this._multiselection &&
			(this._type === "cell" || this._type === "complex") &&
			this._selectedCells.find(
				item =>
					item.row.id === ((row && row.id) || row) && item.column.id === ((col && col.id) || col)
			)
		) {
			this.removeCell((row && row.id) || row, (col && col.id) || col);
			return;
		}

		const oldSelectedCell = this._oldSelectedCell ? this._oldSelectedCell : undefined;
		row = this._grid.data.getItem((row && row.id) || row);
		const colums = this._grid.config.columns.filter(col => !col.hidden);
		if (!col) {
			col = colums[0];
		}
		col = this._grid.getColumn(col.id || col);
		if (!col || !row) {
			return;
		}
		col = col.id ? col : this._grid.getColumn(col);

		if (!this.events.fire(GridSelectionEvents.beforeSelect, [row, col])) return;

		this._selectedCell = { row, column: col };

		this.events.fire(GridSelectionEvents.afterSelect, [row, col]);

		if (this._multiselection && shiftUp && oldSelectedCell) {
			this._oldSelectedCell = oldSelectedCell;
		} else {
			this._oldSelectedCell = this._selectedCell;
		}
		if (this._multiselection) {
			if (shiftUp && !ctrlUp && this._selectedCells.length > 0) {
				let startRowIndex = this._grid.data.getIndex(this._oldSelectedCell.row.id as string);
				let endRowIndex = this._grid.data.getIndex(row.id as string);
				if (startRowIndex > endRowIndex) {
					const temp = startRowIndex;
					startRowIndex = endRowIndex;
					endRowIndex = temp;
				}
				this._selectedCells = [this._oldSelectedCell];
				if (this._type === "cell" || this._type === "complex") {
					const columnsIds = colums.map(e => e.id);
					let startColIndex = columnsIds.indexOf(oldSelectedCell.column.id);
					let endColIndex = columnsIds.indexOf(col.id);
					if (startColIndex !== -1 && endColIndex !== -1) {
						if (startColIndex > endColIndex) {
							const temp = startColIndex;
							startColIndex = endColIndex;
							endColIndex = temp;
						}
						const columns = colums.slice(startColIndex, endColIndex + 1);
						this._grid.data.mapRange(startRowIndex, endRowIndex, item => {
							columns.forEach(column => {
								const cell = { row: item, column };
								if (this._findIndex(cell) === -1) {
									this._selectedCells.push(cell);
								}
							});
						});
					}
				} else {
					this._grid.data.mapRange(startRowIndex, endRowIndex, item => {
						const cell = { row: item, column: col };
						if (this._findIndex(cell) === -1) {
							this._selectedCells.push(cell);
						}
					});
				}
			} else if (ctrlUp && !shiftUp) {
				const cellIndex = this._findIndex();
				if (cellIndex === -1) {
					this._selectedCells.push({
						row: this._selectedCell.row,
						column: this._selectedCell.column,
					});
				} else {
					this._selectedCells.length > 1 && this._selectedCells.splice(cellIndex, 1);
				}
			} else {
				this._selectedCells = [this._selectedCell];
			}
		} else {
			this._selectedCells = [this._selectedCell];
		}
		awaitRedraw().then(() => {
			this._grid.paint();
			this._setBrowserFocus();
		});
	}
	getCell(): ICell {
		return this._selectedCell;
	}
	getCells(): ICell[] {
		return this._selectedCells;
	}
	toHTML() {
		if (this._isUnselected()) {
			return;
		}
		if (this._multiselection) {
			const selection = [];
			const selectedRows = {};

			this._selectedCells.forEach((cell, index, array) => {
				selection.push(
					this._toHTML(
						cell.row,
						cell.column,
						index === array.length - 1 || this._type === "cell" || this._type === "complex",
						selectedRows[cell.row.id as string]
					)
				);
				selectedRows[cell.row.id as string] = true;
			});
			return selection;
		} else {
			return this._toHTML(this._selectedCell.row, this._selectedCell.column, true);
		}
	}
	disable(): void {
		this.removeCell();
		this.config.disabled = true;
		this._grid.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this._grid.paint();
	}
	removeCell(rowId?: Id, colId?: Id): void {
		if (rowId && colId && (this._type === "cell" || this._type === "complex")) {
			const cell = this._selectedCells.find(({ row, column }) => row.id == rowId && column.id == colId);
			cell && this._removeCell(cell.row, cell.column);
		} else if (rowId) {
			const cells = this._selectedCells.filter(({ row }) => row.id == rowId);
			cells.forEach(({ row, column }) => {
				this._removeCell(row, column);
			});
		} else {
			this._removeCells();
		}
		awaitRedraw().then(() => {
			this._grid.paint();
		});
	}
	protected _removeCell(row, col): void {
		if (!row || !col || !row.id || !col.id) return;

		const index = this._selectedCells.findIndex(
			item => item.row.id === row.id && item.column.id === col.id
		);
		if (!this.events.fire(GridSelectionEvents.beforeUnSelect, [row, col])) {
			this._selectedCells[index].$preventedUnselect = true;
			return;
		}
		this._selectedCells.splice(index, 1);
		if (
			this._selectedCell &&
			col.id === this._selectedCell.column.id &&
			row.id === this._selectedCell.row.id
		) {
			this._selectedCell = this._selectedCells[this._selectedCells.length - 1] || undefined;
		}
		if (
			this._oldSelectedCell &&
			this._oldSelectedCell.row.id === row.id &&
			this._oldSelectedCell.column.id === col.id
		) {
			this._oldSelectedCell = undefined;
		}
		this.events.fire(GridSelectionEvents.afterUnSelect, [row, col]);
	}
	protected _removeCells(): void {
		this._selectedCells.forEach(item => {
			this._removeCell(item && item.row, item && item.column);
		});
		this._selectedCells.length &&
			!this._selectedCells.every(item => item.$preventedUnselect) &&
			this._removeCells();
	}
	protected _init() {
		this._grid.events.on(GridEvents.cellClick, (row: IRow, col: ICol, e: MouseEvent) => {
			this.setCell(row, col, e.ctrlKey || e.metaKey, e.shiftKey);
		});
		this._grid.data.events.on(DataEvents.beforeRemove, item => {
			if (item && this._selectedCell && this._selectedCell.row) {
				const index = this._grid.data.getIndex(String(this._selectedCell.row.id));
				const id = this._grid.data.getId(index + 1);
				if (id) {
					this.setCell(id);
				} else {
					const newId = this._grid.data.getId(index - 1);
					if (newId) {
						this.setCell(newId);
					}
				}
				this._grid.paint();
			}
		});
	}
	protected _toHTML(row: IRow, column: ICol, last = false, skipRow = false) {
		const rows = this._grid.data.getRawData(0, -1, null, 2);
		const rowInd = findIndex(rows, obj => obj.id == row.id);
		if (rowInd === -1) return null;
		const leftSplit = this._grid.config.leftSplit;
		const topSplit = this._grid.config.topSplit;
		const colums = this._grid.config.columns.filter(col => !col.hidden);
		const fixedCols = leftSplit ? colums.slice(0, leftSplit) : [];
		const fixedRows = this._grid.config.topSplit
			? this._grid.data.getRawData(0, this._grid.config.topSplit)
			: [];

		const fixedColsIds = fixedCols.map(col => col.id);
		const fixedRowIds = fixedRows.map(row => row.id);
		const fixedColsWidth = fixedCols.reduce((total, coll) => (total += coll.$width), 0);
		const fixedRowsHeight = fixedRows.reduce((total, row) => (total += row.$height), 0);
		const cellRect = this._grid.getCellRect(row.id, column.id);
		const scrollState = this._grid.getScrollState();
		let top = cellRect.y;
		const isFixedRow = fixedRowIds.includes(row.id);
		const isFixedCol = fixedColsIds.includes(column.id);
		const isBehindFixedCols = fixedCols.length && fixedColsWidth > cellRect.x - scrollState.x;
		const isBehindFixedRows = fixedRows.length && fixedRowsHeight > cellRect.y - scrollState.y;
		let width = cellRect.width;
		let height = cellRect.height - 1;
		let minHeight: number | null = null;
		let minWidth: number | null = null;
		let left = isBehindFixedCols ? fixedColsWidth + scrollState.x : cellRect.x;
		if (isBehindFixedCols) {
			width -= (isFixedCol ? 0 : fixedColsWidth) - (cellRect.x - scrollState.x);
		}
		if (isBehindFixedRows) {
			height -= (isFixedRow ? 0 : fixedRowsHeight) - (cellRect.y - scrollState.y) - 1;
			top += cellRect.height - height;
		}
		if (isFixedRow) {
			top = cellRect.y + scrollState.y;
			if (this._grid.getSpan(row.id, column.id)?.rowspan) {
				minHeight = fixedRows
					.splice(this._grid.data.getIndex(row.id))
					.reduce((total, row) => (total += row.$height), 0);
			} else {
				height = topSplit === fixedRowIds.indexOf(row.id) + 1 ? cellRect.height - 1 : cellRect.height;
			}
		}

		if (isFixedCol) {
			left = cellRect.x + scrollState.x;
			if (this._grid.getSpan(row.id, column.id)?.colspan) {
				minWidth = fixedCols
					.splice(this._grid.config.columns?.indexOf(column))
					.reduce((total, col) => (total += col.$width), 0);
			} else {
				width =
					leftSplit === fixedColsIds.indexOf(column.id) + 1 ? cellRect.width - 1 : cellRect.width;
			}
		}

		const totalWidth = this._grid.config.$totalWidth;

		let selectedRowElement = null;

		if ((this._type === "row" || this._type === "complex") && !skipRow) {
			selectedRowElement = el(".dhx_grid-selected-row", {
				style: {
					width: fixedCols.length ? totalWidth - scrollState.x : totalWidth,
					height,
					minHeight,
					minWidth,
					display: height < 0 && !minHeight ? "none" : "flex",
					top,
					left: fixedCols.length ? scrollState.x : 0,
					position: "absolute",
				},
			});
		}

		let selectedCellElement = null;
		if ((this._type === "cell" || this._type === "complex") && last) {
			selectedCellElement = el(".dhx_grid-selected-cell", {
				style: {
					width,
					height,
					top,
					minHeight,
					minWidth,
					left,
					position: "absolute",
					display: (width < 0 && !minWidth) || (height < 0 && !minHeight) ? "none" : "flex",
					borderLeft: isBehindFixedCols && !isFixedCol ? "none" : null,
					borderTop: isBehindFixedRows && !isFixedRow ? "none" : null,
				},
			});
		}

		return el(
			".dhx_grid-selection",
			{
				style: {
					zIndex: 20,
				},
			},
			[selectedRowElement, selectedCellElement]
		);
	}
	protected _isUnselected(): boolean {
		return (
			!this._selectedCell ||
			!this._selectedCell.row ||
			!this._selectedCell.column ||
			this._selectedCells.length === 0
		);
	}
	protected _findIndex(cell: ICell = this._selectedCell) {
		let cellIndex = -1;
		this._selectedCells.some((element: ICell, index: number): boolean => {
			if (this._type === "cell") {
				if (compare(element.row, cell.row) && compare(element.column, cell.column)) {
					cellIndex = index;
					return true;
				}
			} else if (this._type === "row") {
				if (compare(element.row, cell.row)) {
					cellIndex = index;
					return true;
				}
			}
		});
		return cellIndex;
	}
	protected _setBrowserFocus() {
		const gridContainer = this._grid.getRootView().data.getRootNode();
		if (gridContainer) {
			const gridDataContainer = gridContainer.querySelector(".dhx_grid_data");
			if (
				gridDataContainer &&
				this._selectedCell &&
				this._selectedCell.row &&
				this._selectedCell.column
			) {
				const $row = gridDataContainer.querySelector(`[data-dhx-id="${this._selectedCell.row.id}"]`);
				const span = this._grid.getSpan(this._selectedCell.row.id, this._selectedCell.column.id);
				if ($row) {
					const gridSpansContainer = span ? gridContainer.querySelector(".dhx_span-spans") : null;
					const $focusedCell = gridSpansContainer
						? gridSpansContainer.querySelector(
								`[data-dhx-col-id="${span.column}"][data-dhx-id="${span.row}"]`
						  )
						: $row.querySelector(`[data-dhx-col-id="${this._selectedCell.column.id}"]`);
					if ($focusedCell) {
						$focusedCell.tabIndex = 0;
						$focusedCell.focus({ preventScroll: true });
						// $focusedCell.focus();
					}
				}
			}
		}
	}
}
